import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const sections = [
    {
        id: "overview",
        title: "1. Overview",
        body: [
            "This Cancellation & Refund Policy explains how cancellations, notice periods and refunds work for paid PipeGuide subscriptions. It forms part of our Terms of Service and is the policy referenced by our payment processor (Paystack) for cardholder dispute purposes.",
            "All paid plans (Pro and Business) are month-to-month. There are no minimum commitment periods, no early-termination fees, and no annual lock-in. The free Starter plan has no payment obligation and may be cancelled at any time without notice or fee.",
        ],
    },
    {
        id: "billing",
        title: "2. Billing model — month-to-month",
        body: [
            "Pro and Business plans are billed monthly on the same date you subscribed. Your card is charged automatically via Paystack each month for as long as your subscription is active.",
            "All prices are quoted in South African Rand (ZAR) and are exclusive of VAT. International customers may see prices displayed in USD for convenience (at an approximate rate of R18 = $1), but Paystack processes the actual transaction in ZAR. Your card issuer will convert at its prevailing rate.",
            "Plan upgrades, downgrades, and seat changes are pro-rated for the remainder of your current billing month and reflected on your next invoice.",
        ],
    },
    {
        id: "free-trial",
        title: "3. Free Starter plan — 60-day trial",
        body: [
            "New organisations receive 60 days of free access on the Starter plan. No credit card is required to start. After 60 days, call creation is paused until you upgrade to Pro or Business to continue using PipeGuide.",
            "Your data and settings remain intact during and after the trial period. If you upgrade within 12 months of trial expiry, your team picks up exactly where they left off.",
        ],
    },
    {
        id: "notice-period",
        title: "4. Cancellation notice — 30 days",
        body: [
            "All paid-plan cancellations require 30 calendar days' written notice. The notice period begins on the date we receive your cancellation request in writing (email to support@pipeguide.tech, or via the in-app cancellation flow).",
            "During the 30-day notice period, your subscription remains active and your card will be charged once more at the standard rate for the final month. After the notice period ends, your organisation is downgraded to the free Starter tier automatically — no further charges.",
            "There are no early-termination fees and no penalties of any kind. You pay only for the notice month at your normal rate.",
        ],
    },
    {
        id: "refunds",
        title: "5. Refunds",
        body: [
            "Subscription fees already paid for the current or past months are non-refundable. This includes the 30-day notice period, in which your subscription remains active.",
            "Free Starter accounts: there is nothing to refund.",
            "Optional Training & Setup fees (R30,000 / $1,667 for Pro; R55,000 / $3,056 for Business): non-refundable once delivery has commenced. Refundable in full if cancelled before any delivery activity has begun.",
            "Call Packs and Power Caller top-ups: non-refundable once purchased, as credits are added to your plan immediately.",
            "Where a refund is approved at our discretion (e.g. duplicate payment, billing error, or as required by law), it will be processed back to the original payment method via Paystack within 7 business days.",
        ],
    },
    {
        id: "failed-payments",
        title: "6. Failed payments",
        body: [
            "If a scheduled monthly payment fails, we will retry the payment over a 7-day period and notify your account admins. If the payment cannot be collected after 7 days, your subscription is automatically downgraded to the free Starter tier. Your data is preserved — you can resume by updating the payment method and re-subscribing.",
        ],
    },
    {
        id: "how-to-cancel",
        title: "7. How to cancel",
        body: [
            "To cancel, send a written request from your registered admin email address to support@pipeguide.tech, including your organisation name and (optionally) the reason for cancellation.",
            "We will acknowledge your request within one business day and confirm the notice period start date and the cancellation effective date in writing.",
        ],
    },
    {
        id: "disputes",
        title: "8. Chargebacks & disputes",
        body: [
            "Please contact us before raising a chargeback with your bank or card issuer — most issues can be resolved within 24 hours. Where a chargeback is raised in respect of a legitimately invoiced amount (including the 30-day notice month), we will dispute it with Paystack and provide evidence of acceptance of these terms.",
            "Chargeback fees levied on PipeGuide by Paystack as a result of an unsuccessful chargeback may be added to the customer's outstanding balance.",
        ],
    },
    {
        id: "consumer-rights",
        title: "9. Statutory consumer rights",
        body: [
            "Nothing in this policy limits any rights you may have under the South African Consumer Protection Act 68 of 2008 or other applicable laws. Where a statutory right conflicts with these terms, the statutory right prevails.",
        ],
    },
    {
        id: "contact",
        title: "10. Contact",
        body: [
            "Questions about cancellations or refunds? Email support@pipeguide.tech and we will respond within one business day.",
        ],
    },
];

export default function RefundPolicy() {
    return (
        <div data-testid="refund-policy-page" className="min-h-screen flex flex-col bg-white">
            <div className="bg-white border-b border-zinc-200">
                <Navbar tone="light" />
            </div>

            <section className="bg-gradient-to-b from-zinc-50 to-white border-b border-zinc-200">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 md:py-20">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Legal</p>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
                        Cancellation & Refund Policy
                    </h1>
                    <p className="mt-4 text-zinc-600">Last updated: 28 April 2026</p>
                    <div className="mt-6 inline-flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 max-w-2xl">
                        <span className="mt-0.5 h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0" />
                        <span>
                            <strong>At a glance:</strong> Paid plans are month-to-month — cancel anytime with
                            30 days' written notice. No commitment periods, no termination fees.
                            Starter is free for 60 days.
                        </span>
                    </div>
                </div>
            </section>

            <section className="bg-white flex-1">
                <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <aside className="lg:col-span-3">
                        <div className="sticky top-24">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4">On this page</p>
                            <nav className="space-y-2">
                                {sections.map((s) => (
                                    <a key={s.id} href={`#${s.id}`} data-testid={`refund-nav-${s.id}`} className="block text-sm text-zinc-600 hover:text-brand-primary">
                                        {s.title}
                                    </a>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    <article className="lg:col-span-9">
                        {sections.map((s) => (
                            <div key={s.id} id={s.id} data-testid={`refund-section-${s.id}`} className="mb-10 scroll-mt-24">
                                <h2 className="font-display text-2xl font-bold text-zinc-900">{s.title}</h2>
                                {s.body.map((p, i) => (
                                    <p key={i} className="mt-4 text-zinc-700 leading-relaxed">{p}</p>
                                ))}
                            </div>
                        ))}
                    </article>
                </div>
            </section>

            <Footer />
        </div>
    );
}
